/**
 * Wake — the short memory of where creatures have been, for anything that has to recover.
 *
 * The algae asks a simple question every frame: how hard is something pressing on this patch
 * of seabed? Answering with the creatures' CURRENT positions gives grass that snaps upright
 * the instant an animal clears it. Answering with a trail of recent positions, each fading,
 * gives grass that closes behind it — and a visible swathe where it went.
 *
 * That is all this is: a fixed-size ring of recent samples per tracked creature, aged out and
 * flattened into the array the shader wants. No allocation after construction, because it runs
 * on the render path.
 */

/** samples kept per creature; more is a longer wake and more shader work */
const TRAIL = 6;
/* Seconds between samples — TRAIL * this is how long the bed remembers, so the memory is
   lengthened by keeping MORE samples rather than by spacing them further apart. Spacing is
   fixed by geometry, not by taste: a shark at cruise covers 2.4 u between samples against a
   3.6 u press radius, and widening the gap past the radius would break the swathe into a row
   of separate dents. */
const INTERVAL = 0.17;

export interface Disturber {
  x: number;
  y: number;
  z: number;
  radius: number;
  amount: number;
  /* Unit heading the creature was travelling in when it laid this sample down, or (0,0) if it
     was not moving. The grass needs it: a push that is purely radial REVERSES as the animal
     crosses a blade, because "away from the animal" is forward one frame and backward the
     next. Combing along the direction of travel instead is what moving water does, and there
     is no reversal anywhere in it. */
  hx: number;
  hz: number;
}

interface Track {
  /** ring of recent samples, newest at `head` */
  x: Float32Array;
  y: Float32Array;
  z: Float32Array;
  t: Float32Array;
  /** unit heading at each sample */
  hx: Float32Array;
  hz: Float32Array;
  head: number;
  filled: number;
  radius: number;
  next: number;
}

export class Wake {
  private readonly tracks = new Map<number, Track>();
  private readonly out: Disturber[] = [];

  /**
   * Record where a creature is now. Cheap to call every frame for the same key: samples are
   * only committed every INTERVAL, and the newest is updated in place between commits so the
   * leading edge of the wake is exact rather than up to a sixth of a second stale.
   */
  track(key: number, x: number, y: number, z: number, radius: number, now: number): void {
    let t = this.tracks.get(key);
    if (!t) {
      t = {
        x: new Float32Array(TRAIL),
        y: new Float32Array(TRAIL),
        z: new Float32Array(TRAIL),
        t: new Float32Array(TRAIL),
        hx: new Float32Array(TRAIL),
        hz: new Float32Array(TRAIL),
        head: 0, filled: 0, radius, next: now,
      };
      this.tracks.set(key, t);
    }
    t.radius = radius;
    if (now >= t.next) {
      t.head = (t.head + 1) % TRAIL;
      t.filled = Math.min(TRAIL, t.filled + 1);
      t.next = now + INTERVAL;
    }
    t.x[t.head] = x;
    t.y[t.head] = y;
    t.z[t.head] = z;
    t.t[t.head] = now;

    /* Heading, measured against the previous COMMITTED sample rather than the previous frame.
       A delta over a 16 ms step is mostly jitter; over the sample interval it is the direction
       the animal is actually going. A creature that has not moved far enough to tell leaves
       the heading at zero, and the grass falls back to pushing radially. */
    if (t.filled > 1) {
      const prev = (t.head - 1 + TRAIL) % TRAIL;
      const dx = x - t.x[prev];
      const dz = z - t.z[prev];
      const len = Math.hypot(dx, dz);
      if (len > 0.05) {
        t.hx[t.head] = dx / len;
        t.hz[t.head] = dz / len;
      }
    }
  }

  forget(key: number): void {
    this.tracks.delete(key);
  }

  /**
   * Flatten every track into the shader's array, newest first.
   *
   * @param now      wall clock, for ageing
   * @param limit    the shader's array size; the closest creatures win when there are more
   * @param cx/@param cz the viewer, so the ones that matter are the ones kept
   */
  build(now: number, limit: number, cx: number, cz: number): Disturber[] {
    this.out.length = 0;
    const life = TRAIL * INTERVAL;

    for (const t of this.tracks.values()) {
      for (let k = 0; k < t.filled; k++) {
        const i = (t.head - k + TRAIL * 2) % TRAIL;
        const age = now - t.t[i];
        if (age > life) continue;
        /* RELEASE, NOT DECAY — this curve is the whole feel of the bed.

           It was (1 - age/life) squared, which is falling at full speed the instant the animal
           arrives: the blade starts springing back while the tail is still going over it, and
           the bed reads as snapping rather than flowing. A smoothstep has zero slope at BOTH
           ends, so the grass holds where it was put and then lets go slowly and settles. That
           plateau is what inertia looks like from the outside, and it is free — no per-blade
           state, just a different shape on a number that was already being computed. */
        const f = 1 - age / life;
        const hold = f * f * (3 - 2 * f);
        /* A short lead-in so the front of the wake builds instead of hitting. Deliberately
           small and starting well above zero: push the onset any further back and there is a
           pocket of upright grass at the animal's nose that its own body then swims through. */
        const lead = 0.66 + 0.34 * Math.min(1, age / 0.13);
        this.out.push({
          x: t.x[i], y: t.y[i], z: t.z[i],
          radius: t.radius, amount: hold * lead,
          hx: t.hx[i], hz: t.hz[i],
        });
      }
    }

    if (this.out.length > limit) {
      // Keep what is near the camera: a trail forty units away cannot be seen bending anything.
      this.out.sort((a, b) => {
        const da = (a.x - cx) ** 2 + (a.z - cz) ** 2;
        const db = (b.x - cx) ** 2 + (b.z - cz) ** 2;
        return da - db;
      });
      this.out.length = limit;
    }
    return this.out;
  }

  clear(): void {
    this.tracks.clear();
  }
}
